/**
 * MUSKLE category quick discovery — family-aware (Phase 3D).
 * Binds filter chips to MuskleCatalogFilters on parent hubs.
 * On child collections: filter tabs are plain links to parent+mc_*.
 * Collection chips are plain anchors. Active state from current handle.
 */
(() => {
  const ROOT_SEL = '[data-muskle-category-quick-discovery]';
  const PARAM = { subs: 'mc_sub', forms: 'mc_form', cats: 'mc_cat', ings: 'mc_ing', brands: 'mc_brand' };

  function waitForApi(timeoutMs = 8000) {
    return new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        if (window.MuskleCatalogFilters && typeof window.MuskleCatalogFilters.setGroup === 'function') {
          resolve(window.MuskleCatalogFilters);
          return;
        }
        if (window.MuskleCatalogFilters && typeof window.MuskleCatalogFilters.clear === 'function') {
          resolve(window.MuskleCatalogFilters);
          return;
        }
        if (Date.now() - start > timeoutMs) {
          resolve(window.MuskleCatalogFilters || null);
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  function readParamSet(key) {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(key) || '';
    return new Set(
      raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );
  }

  function catalogActiveFromUrl() {
    const url = new URL(window.location.href);
    return Object.values(PARAM).some((k) => url.searchParams.has(k));
  }

  function stateSets() {
    const api = window.MuskleCatalogFilters;
    let subs = readParamSet(PARAM.subs);
    let forms = readParamSet(PARAM.forms);
    if (api && typeof api.getState === 'function') {
      try {
        const st = api.getState();
        if (st?.subs) subs = new Set(st.subs);
        if (st?.forms) forms = new Set(st.forms);
      } catch (_) {
        /* ignore */
      }
    }
    return { subs, forms };
  }

  function syncRoot(root) {
    const current = root.getAttribute('data-muskle-qd-current') || '';
    const parent = root.getAttribute('data-muskle-qd-parent') || '';
    const isChild = root.getAttribute('data-muskle-qd-child') === '1' || (current && parent && current !== parent);
    const { subs, forms } = stateSets();
    const anyFilter = catalogActiveFromUrl() || subs.size > 0 || forms.size > 0;

    root.querySelectorAll('[data-muskle-discovery-type="collection"]').forEach((el) => {
      const v = el.getAttribute('data-muskle-discovery-value') || '';
      const on = !!v && v === current;
      el.classList.toggle('is-active', on);
      el.setAttribute('aria-current', on ? 'page' : 'false');
    });

    root.querySelectorAll('[data-muskle-qd-action="reset"]').forEach((el) => {
      const allOn = !isChild && !anyFilter;
      el.classList.toggle('is-active', allOn);
      if (el.tagName === 'A') {
        el.setAttribute('aria-current', allOn ? 'page' : 'false');
      } else {
        el.setAttribute('aria-pressed', allOn ? 'true' : 'false');
      }
    });

    root.querySelectorAll('[data-muskle-qd-action="filter"], [data-muskle-qd-action="filter-sub"]').forEach((el) => {
      const group = el.getAttribute('data-muskle-qd-group') || 'subs';
      const value =
        el.getAttribute('data-muskle-qd-value') ||
        el.getAttribute('data-muskle-qd-sub') ||
        '';
      const set = group === 'forms' ? forms : subs;
      const on = value && set.has(value);
      el.classList.toggle('is-active', !!on);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  async function onFilterClick(root, btn) {
    const group = btn.getAttribute('data-muskle-qd-group') || 'subs';
    const value =
      btn.getAttribute('data-muskle-qd-value') ||
      btn.getAttribute('data-muskle-qd-sub') ||
      '';
    if (!value) return;

    const api = await waitForApi();
    const currentlyOn = btn.getAttribute('aria-pressed') === 'true';
    const apiGroup = group === 'forms' ? 'forms' : 'subs';

    if (api && typeof api.setGroup === 'function') {
      api.setGroup(apiGroup, currentlyOn ? [] : [value]);
      syncRoot(root);
      return;
    }

    const param = group === 'forms' ? PARAM.forms : PARAM.subs;
    const url = new URL(window.location.href);
    if (currentlyOn) url.searchParams.delete(param);
    else url.searchParams.set(param, value);
    url.searchParams.delete('page');
    history.pushState({ muskleCatalog: true, muskleQd: true }, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
    syncRoot(root);
  }

  async function onResetClick(root, el) {
    const current = root.getAttribute('data-muskle-qd-current') || '';
    const parent = root.getAttribute('data-muskle-qd-parent') || '';
    const isChild = root.getAttribute('data-muskle-qd-child') === '1' || (current && parent && current !== parent);
    if (isChild && el && el.tagName === 'A' && el.getAttribute('href')) {
      window.location.assign(el.getAttribute('href'));
      return;
    }

    const api = await waitForApi();
    if (api && typeof api.clear === 'function') {
      api.clear();
      syncRoot(root);
      return;
    }
    if (el && el.tagName === 'A' && el.getAttribute('href')) {
      window.location.assign(el.getAttribute('href'));
      return;
    }
    const url = new URL(window.location.href);
    Object.values(PARAM).forEach((k) => url.searchParams.delete(k));
    history.pushState({ muskleCatalog: true, muskleQd: true }, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
    syncRoot(root);
  }

  function bind(root) {
    if (root.dataset.mqdBound === '1') return;
    root.dataset.mqdBound = '1';

    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-muskle-qd-action]');
      if (!btn || !root.contains(btn)) return;
      e.preventDefault();
      const action = btn.getAttribute('data-muskle-qd-action');
      if (action === 'filter' || action === 'filter-sub') onFilterClick(root, btn);
      else if (action === 'reset') onResetClick(root, btn);
    });

    document.addEventListener('muskle:catalog-state', () => syncRoot(root));
    window.addEventListener('popstate', () => {
      syncRoot(root);
    });

    waitForApi().then(() => syncRoot(root));
    syncRoot(root);
  }

  function boot() {
    document.querySelectorAll(ROOT_SEL).forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
